import React from 'react'
import { cn } from '@/lib/utils'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@digital-twins/design-system/components/ui/hover-card'
import { INTERACTION_LIMITS } from '../../../foundations/slide-limits'
/**
 * Metric item for tooltip display
 */
export interface TooltipMetric {
  name: string
  value: number
  /** Optional display format (e.g., 'percentage', 'rating') */
  format?: 'percentage' | 'rating' | 'score' | 'count'
}

/**
 * Theme item for tooltip display.
 * Named 'TooltipCode' for backward compatibility - now contains LLM-discovered themes.
 */
export interface TooltipCode {
  /** Theme name (named code_id for backward compatibility) */
  code_id: string
  /** Theme label (usually same as code_id for themes) */
  label?: string
  /** Share as decimal (0-1) */
  share: number
}

/**
 * Quote item for tooltip (matches spec Quote object)
 */
export interface TooltipQuote {
  quote_id?: string
  text: string
  full_text?: string
  persona_id?: string
  segment_id?: string
  initials?: string
  respondentName?: string
}

/**
 * ChartTooltip contract per spec §v.0
 * Provides tooltip data for chart elements keyed by object ID
 */
export interface ChartTooltipData {
  /** Object type (e.g., 'item', 'theme', 'entity', 'global', or any template-specific type) */
  object_type: string
  /** Object ID for filtering */
  object_id: string
  /** Display label for the tooltip header */
  label: string
  /** Key metrics to display */
  top_metrics?: TooltipMetric[]
  /** Top codes with shares */
  top_codes?: TooltipCode[]
  /** Quotes (6-12 filtered by object_id + optional code_id) */
  quotes?: TooltipQuote[]
  /** Optional image preview URL for this object */
  imageUrl?: string | null
  /** Optional video preview URL for this object */
  videoUrl?: string | null
  /** Optional thumbnail URL (used as poster/fallback) */
  thumbnailUrl?: string | null
  /** Optional short hook/copy line */
  hook?: string | null
  /** Optional CTA text */
  cta?: string | null
  /** Optional callback to open evidence drawer */
  onEvidenceClick?: () => void
}

export interface ChartTooltipProps {
  data: ChartTooltipData
  /** The element to wrap with tooltip */
  children: React.ReactNode
  /** Align popup */
  align?: 'start' | 'center' | 'end'
  /** Side of trigger */
  side?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  /**
   * Max width for code/theme label text before truncation.
   * Increase for slides with longer theme names.
   * @default '8rem'
   */
  codeLabelMaxWidth?: string
}

function formatMetricValue(value: number, format?: string): string {
  if (!Number.isFinite(value)) return '—'
  switch (format) {
    case 'percentage':
      return `${Math.round(value * 100)}%`
    case 'rating':
      return value.toFixed(1)
    case 'count':
      return Math.round(value).toString()
    case 'score':
    default:
      return value <= 1 ? `${Math.round(value * 100)}%` : value.toFixed(1)
  }
}

/**
 * ChartTooltip - Enhanced tooltip for chart elements
 * Implements spec §v.0 tooltip contract:
 * - Shows top_metrics (metric_name, value)
 * - Shows top_codes (code_id, share)
 * - Shows quotes (6-12 filtered by object_id)
 * - Provides evidence_link to open quote drawer
 */
export function ChartTooltip({
  data,
  children,
  align = 'center',
  side = 'bottom',
  className,
  codeLabelMaxWidth = '8rem',
}: ChartTooltipProps) {
  const hasMetrics = data.top_metrics && data.top_metrics.length > 0
  const hasCodes = data.top_codes && data.top_codes.length > 0
  const hasQuotes = data.quotes && data.quotes.length > 0
  const hasMedia = Boolean(data.imageUrl || data.videoUrl || data.thumbnailUrl || data.hook || data.cta)
  const hasContent = hasMetrics || hasCodes || hasQuotes || hasMedia

  if (!hasContent) {
    return <>{children}</>
  }

  return (
    <HoverCard openDelay={INTERACTION_LIMITS.tooltipOpenDelay} closeDelay={INTERACTION_LIMITS.tooltipCloseDelay}>
      <HoverCardTrigger asChild>
        <div className={cn('cursor-pointer w-full h-full', className)}>{children}</div>
      </HoverCardTrigger>
      <HoverCardContent
        align={align}
        side={side}
        sideOffset={8}
        className={cn(
          'z-toast',
          'bg-[color:var(--slide-card-bg)]',
          'border border-[color:var(--slide-border-soft)]',
          'p-0 overflow-hidden'
        )}
        style={{
          width: 'min(20rem, 80vw)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-md)',
          maxHeight: 'min(20rem, 60vh)',
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <div
          className="border-b border-[color:var(--slide-border-soft)] bg-[color:var(--slide-neutral-light)]"
          style={{ padding: 'var(--spacing-3)' }}
        >
          <div className="text-[color:var(--slide-text-primary)]" style={{ fontSize: 'var(--font-size-caption)', fontWeight: 'var(--font-weight-bold)' }}>
            {data.label}
          </div>
          <div className="text-[color:var(--slide-text-muted)] uppercase" style={{ fontSize: 'var(--font-size-label)' }}>
            {data.object_type}
          </div>
        </div>

        <div className="grid" style={{ padding: 'var(--spacing-3)', rowGap: 'var(--spacing-3)' }}>
          {/* Media Section */}
          {hasMedia && (
            <div className="grid" style={{ rowGap: 'var(--spacing-2)' }}>
              {data.videoUrl ? (
                <video
                  src={data.videoUrl}
                  poster={data.thumbnailUrl || data.imageUrl || undefined}
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full object-cover"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    maxHeight: '8.5rem',
                    background: 'var(--slide-neutral-light)',
                  }}
                />
              ) : data.imageUrl || data.thumbnailUrl ? (
                <img
                  src={data.imageUrl || data.thumbnailUrl || ''}
                  alt={data.label}
                  className="w-full object-cover"
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    maxHeight: '8.5rem',
                    background: 'var(--slide-neutral-light)',
                  }}
                />
              ) : null}
              {data.hook ? (
                <div className="text-[color:var(--slide-text-secondary)]" style={{ fontSize: 'var(--font-size-label)', lineHeight: 'var(--line-height-normal)' }}>
                  {data.hook}
                </div>
              ) : null}
              {data.cta ? (
                <div
                  className="inline-flex items-center w-fit bg-[color:var(--slide-neutral-light)] text-[color:var(--slide-text-primary)]"
                  style={{
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-label)',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  {data.cta}
                </div>
              ) : null}
            </div>
          )}

          {/* Metrics Section */}
          {hasMetrics && (
            <div>
              <div
                className="uppercase text-[color:var(--slide-text-muted)]"
                style={{ fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--spacing-2)' }}
              >
                Key Metrics
              </div>
              <div className="grid grid-cols-2" style={{ gap: 'var(--spacing-2)' }}>
                {data.top_metrics!.slice(0, INTERACTION_LIMITS.maxFeedbackItems).map((m, idx) => (
                  <div key={`metric-${idx}`} className="flex justify-between items-baseline">
                    <span
                      className="text-[color:var(--slide-text-secondary)] truncate"
                      style={{ fontSize: 'var(--font-size-label)', marginRight: 'var(--spacing-2)' }}
                    >
                      {m.name}
                    </span>
                    <span className="text-[color:var(--slide-text-primary)]" style={{ fontSize: 'var(--font-size-caption)', fontWeight: 'var(--font-weight-semibold)' }}>
                      {formatMetricValue(m.value, m.format)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Codes Section */}
          {hasCodes && (
            <div>
              <div
                className="uppercase text-[color:var(--slide-text-muted)]"
                style={{ fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--spacing-2)' }}
              >
                Top Themes
              </div>
              <div className="flex flex-wrap" style={{ gap: 'var(--spacing-1)' }}>
                {data.top_codes!.slice(0, INTERACTION_LIMITS.maxFeedbackItems).map((c, idx) => (
                  <span
                    key={`code-${idx}`}
                    className="inline-flex items-center bg-[color:var(--slide-neutral-light)]"
                    style={{
                      padding: 'var(--spacing-1) var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-label)',
                    }}
                  >
                    <span
                      className="text-[color:var(--slide-text-secondary)] truncate"
                      style={{ maxWidth: codeLabelMaxWidth }}
                    >
                      {c.label || c.code_id}
                    </span>
                    <span
                      className="text-[color:var(--slide-text-muted)]"
                      style={{ marginLeft: 'var(--spacing-1)', fontWeight: 'var(--font-weight-semibold)' }}
                    >
                      {Math.round(c.share * 100)}%
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Quotes Section */}
          {hasQuotes && (
            <div>
              <div
                className="uppercase text-[color:var(--slide-text-muted)]"
                style={{ fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--spacing-2)' }}
              >
                Evidence ({data.quotes!.length} quotes)
              </div>
              <div className="grid" style={{ rowGap: 'var(--spacing-2)' }}>
                {data.quotes!.slice(0, INTERACTION_LIMITS.minQuotesForDrawer).map((q, idx) => (
                  <div key={`quote-${idx}`} className="flex" style={{ gap: 'var(--spacing-2)' }}>
                    <div
                      className="rounded-full flex items-center justify-center text-white shrink-0"
                      style={{
                        width: 'var(--spacing-6)',
                        height: 'var(--spacing-6)',
                        fontSize: 'var(--font-size-label)',
                        fontWeight: 'var(--font-weight-semibold)',
                        background: 'linear-gradient(135deg, var(--slide-text-primary) 0%, var(--slide-text-secondary) 100%)'
                      }}
                    >
                      {(q.initials || 'P').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="italic text-[color:var(--slide-text-secondary)]" style={{ fontSize: 'var(--font-size-label)', lineHeight: 'var(--line-height-relaxed)' }}>
                        "{q.text}"
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {data.onEvidenceClick && data.quotes!.length > INTERACTION_LIMITS.minQuotesForDrawer && (
                <button
                  type="button"
                  onClick={data.onEvidenceClick}
                  className="text-[color:var(--slide-accent-alt)] hover:underline"
                  style={{ fontSize: 'var(--font-size-label)', fontWeight: 'var(--font-weight-semibold)', marginTop: 'var(--spacing-2)' }}
                >
                  View all {data.quotes!.length} quotes →
                </button>
              )}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

export default ChartTooltip
